import { useEffect, useState } from "react";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { Field, HierarchyType, PlaceLimitedValueItem } from "@/entities/form-metadata";
import { useValidation } from "@/shared/forms";
import { useCSSSafeId } from "@/shared/hooks/useCSSSafeId";
import styles from "./HierarchicSelect.module.scss";

interface HierarchicSelectProps {
    field: Field<HierarchyType>;
    testId: string;
    value?: number;
    onChangeFilter: (selectedItem: PlaceLimitedValueItem | null) => Promise<void>;
}

export const HierarchicSelect = ({ field, value, testId, onChangeFilter }: HierarchicSelectProps) => {
    const id = useCSSSafeId();
    const [selectedGpid, setSelectedGpid] = useState<string>("");

    const isRequired = field.features?.required;

    useValidation(id, { errorLocation: "callout", rules: { required: isRequired } });

    useEffect(() => {
        setSelectedGpid(value ? String(value) : "");
    }, [value]);

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;
        setSelectedGpid(selectedValue);

        if (selectedValue === "") {
            onChangeFilter(null);
            return;
        }

        const selectedItem = field.meta.placeLimitedValues?.find(
            (limitedValue) => String(limitedValue.gpid) === selectedValue,
        );

        invariant(selectedItem, "selectedItem - should be defined");
        onChangeFilter(selectedItem);
    };

    const getSelectClassName = () => {
        if (selectedGpid) return "success";
        if (isRequired) return "error";
        return "";
    };

    return (
        <div>
            <div>
                <label htmlFor={id} className={isRequired ? styles.required : ""}>
                    {field.label}
                </label>
            </div>
            <div>
                <select
                    id={id}
                    required={isRequired}
                    value={selectedGpid}
                    onChange={handleSelect}
                    className={getSelectClassName()}
                    data-testid={testId}
                >
                    <option value="">{/* Empty option */}</option>
                    {field.meta.placeLimitedValues?.map((v) => (
                        <option key={v.value} value={String(v.gpid)}>
                            {v.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
